import { storage } from '../utils'
import { performFetch, submitItem, deleteItem } from './utils'
import { browserHistory } from 'react-router'
import * as api from '../constants/APIEndpoints'

const HTTP_200_OK = 200
const ONE_HUNDRED_PERCENT = 100

/**
 * Load personal data for a given person (by uuid).
 *
 * @param {string} crew_uuid The UUID of the crewmember to retrieve the personal data of.
 * @returns {Function} An action creator thunk.
 */
export function loadPersonal(crew_uuid) {
    return performFetch({
        type: 'LOAD_PERSONAL',
        urlData: { crew_uuid },
    })
}

/**
 * Send a PATCH request to the crewmember personal API endpoint. The idea here being that we only
 * supply a subset of the full data in a given request which allows for save-as-you-go.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the personal data for.
 * @returns {Function} An action creator thunk.
 */
export function patchPersonal(data, crew_uuid) {
    return performFetch({
        type: 'PATCH_PERSONAL',
        method: 'PATCH',
        data,
        urlData: { crew_uuid },
    })
}

/**
 * Send a POST request to the crewmember personal API endpoint with all personal data.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the personal data for.
 * @returns {Function} An action creator thunk.
 */
export function submitPersonal(data, crew_uuid) {
    return performFetch({
        type: 'SUBMIT_PERSONAL',
        urlData: { crew_uuid },
        data,
        onSuccess: (data) => {
            if (data.onboarding_complete) {
                browserHistory.push('/profile')
            } else {
                browserHistory.push('/experience')
            }

            return { type: 'SUBMIT_PERSONAL_SUCCESS', data }
        },
        onSuccessActions: [
            { type: 'ONBOARDING_STEP_COMPLETE', step: 'personal' },
        ],
    })
}

/**
 * Load experience data for a given person (by uuid).
 *
 * @param {string} crew_uuid The UUID of the crewmember to retrieve the experience data of.
 * @returns {Function} An action creator thunk.
 */
export function loadExperience(crew_uuid) {
    return performFetch({
        type: 'LOAD_EXPERIENCE',
        urlData: { crew_uuid },
    })
}

/**
 * Send a PATCH request to the crewmember experience API endpoint. The idea here being that we only
 * supply a subset of the full data in a given request which allows for save-as-you-go.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the experience data for.
 * @returns {Function} An action creator thunk.
 */
export function patchExperience(data, crew_uuid) {
    return performFetch({
        type: 'PATCH_EXPERIENCE',
        method: 'PATCH',
        data,
        urlData: { crew_uuid },
    })
}

/**
 * Send a POST request to the crewmember experience API endpoint with all experience data.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the experience data for.
 * @param {Object} onboardingData The personal data for the crewmember. This is used solely for
 *                                redirection-after-save logic (to determine if the onboarding is
 *                                complete or not).
 * @returns {Function} An action creator thunk.
 */
export function submitExperience(data, crew_uuid, onboardingData) {
    return performFetch({
        type: 'SUBMIT_EXPERIENCE',
        urlData: { crew_uuid },
        data,
        onSuccess: (data) => {
            if (onboardingData.onboarding_complete) {
                browserHistory.push('/profile')
            } else {
                browserHistory.push('/crew-positions')
            }

            return { type: 'SUBMIT_EXPERIENCE_SUCCESS', data }
        },
        onSuccessActions: [
            { type: 'ONBOARDING_STEP_COMPLETE', step: 'experience' },
        ],
    })
}

export function updateCrewPositions(data) {
    return performFetch({
        type: 'UPDATE_CREW_POSITIONS',
        urlData: { uuid: data.uuid },
        data,
        onSuccess: (data) => {
            browserHistory.push('/gear/primary')

            return { type: 'UPDATE_CREW_POSITIONS_SUCCESS', data }
        },
        onSuccessActions: [
            { type: 'ONBOARDING_STEP_COMPLETE', step: 'crew_positions' },
        ],
    })
}

/**
 * Submit the add/edit additional city form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} city_uuid The UUID of the additional city.
 * @returns {Function} An action creator thunk.
 */
export function submitAdditionalCity(data, crew_uuid, city_uuid) {
    return submitItem('ADDITIONAL_CITY', data, crew_uuid, city_uuid)
}

/**
 * Delete an additional city from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} city_uuid The UUID of the additional city.
 * @returns {Function} An action creator thunk.
 */
export function deleteAdditionalCity(crew_uuid, city_uuid) {
    return deleteItem('ADDITIONAL_CITY', crew_uuid, city_uuid)
}

export function uploadPhoto(file, uuid) {
    // Create a form data object to handle the file
    const formData = new FormData()

    formData.append('photo', file)

    return (dispatch) => {
        dispatch({ type: 'LOADING' })

        const url = `${ api.UPLOAD_PHOTO_URL }${ uuid }`

        const xhr = new XMLHttpRequest()

        xhr.onload = () => {
            dispatch({ type: 'LOADING_COMPLETE' })

            if (xhr.status === HTTP_200_OK) {
                dispatch({ type: 'UPLOAD_PHOTO_SUCCESS', data: JSON.parse(xhr.responseText) })
            } else {
                dispatch({ type: 'UPLOAD_PHOTO_ERROR', errors: JSON.parse(xhr.responseText) })
            }
        }

        xhr.onerror = () => {
            console.log('Photo upload error.')
        }

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                const percent = Math.round((event.loaded / event.total) * ONE_HUNDRED_PERCENT)

                dispatch({ type: 'UPLOAD_PHOTO_PROGRESS', percent })
            }
        }

        xhr.open('POST', url)
        xhr.setRequestHeader('Authorization', `Token ${ storage.getItem('authToken') }`)
        xhr.send(formData)
    }
}

export function uploadPhotoError(errors) {
    return { type: 'UPLOAD_PHOTO_ERROR', errors }
}

export function uploadResume(file, crew_uuid) {
    // Create a form data object to handle the file
    const formData = new FormData()

    formData.append('resume', file)

    return (dispatch) => {
        dispatch({ type: 'LOADING' })

        const url = `${ api.UPLOAD_RESUME_URL }${ crew_uuid }`

        const xhr = new XMLHttpRequest()

        xhr.onload = () => {
            dispatch({ type: 'LOADING_COMPLETE' })

            if (xhr.status === HTTP_200_OK) {
                dispatch({ type: 'UPLOAD_RESUME_SUCCESS', data: JSON.parse(xhr.responseText) })
            } else {
                dispatch({ type: 'UPLOAD_RESUME_ERROR', errors: JSON.parse(xhr.responseText) })
            }
        }

        xhr.onerror = () => {
            console.log('Resume upload error.')
        }

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                const percent = Math.round((event.loaded / event.total) * ONE_HUNDRED_PERCENT)

                dispatch({ type: 'UPLOAD_RESUME_PROGRESS', percent })
            }
        }

        xhr.open('POST', url)
        xhr.setRequestHeader('Authorization', `Token ${ storage.getItem('authToken') }`)
        xhr.send(formData)
    }
}

export function uploadResumeError(errors) {
    return { type: 'UPLOAD_RESUME_ERROR', errors }
}



// WEBPACK FOOTER //
// ./app/actions/personal.js